import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IText } from 'fabric';
import { useDesignStore } from '@/stores';
import { cn } from '@/lib/utils';
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  ChevronDown,
  Palette,
  Copy,
  Trash2,
} from 'lucide-react';
import { ColorPicker } from './ColorPicker';
import { ensureCanvasFontsLoaded } from '@/utils/canvasFonts';
import { FONTS_COMPACT } from '@/config/fonts';

/**
 * TextPropertiesBar - Horizontal bar at the bottom for text editing
 *
 * Shows when a text object is selected on the canvas
 */
export function TextPropertiesBar() {
  const { t } = useTranslation();
  const { canvas, selectedObjects, saveToHistory } = useDesignStore();

  // Text properties state
  const [fontFamily, setFontFamily] = useState('Roboto, sans-serif');
  const [fontSize, setFontSize] = useState(32);
  const [textColor, setTextColor] = useState('#000000');
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>(
    'left'
  );
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontDropdown, setShowFontDropdown] = useState(false);

  // Check if a text object is selected
  const selectedText = selectedObjects.find(
    (obj) =>
      obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox'
  ) as IText | undefined;

  // Sync state with selected text object
  useEffect(() => {
    if (!selectedText) return;

    setFontFamily(selectedText.fontFamily || 'Roboto, sans-serif');
    setFontSize(selectedText.fontSize || 32);
    setTextColor((selectedText.fill as string) || '#000000');
    setTextAlign(
      (selectedText.textAlign as 'left' | 'center' | 'right') || 'left'
    );
    setIsBold(selectedText.fontWeight === 'bold');
    setIsItalic(selectedText.fontStyle === 'italic');
    setIsUnderline(selectedText.underline || false);
  }, [selectedText]);

  // Update text object
  const updateTextObject = useCallback(
    (props: Partial<IText>) => {
      if (!selectedText || !canvas) return;

      selectedText.set(props);
      canvas.requestRenderAll();
      // A new family, weight, style or glyph may need a face that is still
      // loading: re-measure once it arrives instead of keeping the fallback.
      void ensureCanvasFontsLoaded(canvas);
      saveToHistory('text-edit');
    },
    [selectedText, canvas, saveToHistory]
  );

  // Handlers
  const handleFontChange = (value: string) => {
    setFontFamily(value);
    updateTextObject({ fontFamily: value });
    setShowFontDropdown(false);
  };

  const handleFontSizeChange = (delta: number) => {
    const newSize = Math.max(8, Math.min(200, fontSize + delta));
    setFontSize(newSize);
    updateTextObject({ fontSize: newSize });
  };

  const handleColorChange = (value: string) => {
    setTextColor(value);
    updateTextObject({ fill: value });
    setShowColorPicker(false);
  };

  const handleAlignChange = (value: 'left' | 'center' | 'right') => {
    setTextAlign(value);
    updateTextObject({ textAlign: value });
  };

  const handleBoldToggle = () => {
    const newValue = !isBold;
    setIsBold(newValue);
    updateTextObject({ fontWeight: newValue ? 'bold' : 'normal' });
  };

  const handleItalicToggle = () => {
    const newValue = !isItalic;
    setIsItalic(newValue);
    updateTextObject({ fontStyle: newValue ? 'italic' : 'normal' });
  };

  const handleUnderlineToggle = () => {
    const newValue = !isUnderline;
    setIsUnderline(newValue);
    updateTextObject({ underline: newValue });
  };

  const handleDuplicate = async () => {
    if (!canvas || !selectedText) return;
    const cloned = await selectedText.clone();
    cloned.set({ left: (cloned.left || 0) + 20, top: (cloned.top || 0) + 20 });
    canvas.add(cloned);
    canvas.setActiveObject(cloned);
    canvas.requestRenderAll();
    saveToHistory('duplicate');
  };

  const handleDelete = () => {
    if (!canvas || !selectedText) return;
    canvas.remove(selectedText);
    canvas.requestRenderAll();
    saveToHistory('delete');
  };

  // Get current font name
  const currentFont =
    FONTS_COMPACT.find((f) => f.value === fontFamily) || FONTS_COMPACT[0];

  // Don't render if no text is selected
  if (!selectedText) {
    return null;
  }

  return (
    <div className="absolute bottom-0 left-0 right-0 z-50 bg-paper border-t border-line ring-1 ring-line">
      <div className="h-14 px-4 flex items-center gap-4">
        {/* Font Selector */}
        <div className="relative">
          <button
            onClick={() => setShowFontDropdown(!showFontDropdown)}
            className="flex items-center gap-2 px-3 py-1.5 border border-line rounded-control hover:border-primary min-w-[120px]"
          >
            <span
              className="text-sm"
              style={{ fontFamily: currentFont?.value }}
            >
              {currentFont?.name}
            </span>
            <ChevronDown className="w-4 h-4 text-mute" />
          </button>

          {showFontDropdown && (
            <div className="absolute bottom-full left-0 mb-2 bg-paper-2 rounded-control ring-1 ring-line border border-line py-1 min-w-[150px] max-h-48 overflow-y-auto z-50">
              {FONTS_COMPACT.map((font) => (
                <button
                  key={font.value}
                  onClick={() => handleFontChange(font.value)}
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm hover:bg-primary-50',
                    fontFamily === font.value && 'bg-primary-50 text-primary'
                  )}
                  style={{ fontFamily: font.value }}
                >
                  {font.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-paper-3" />

        {/* Font Size */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleFontSizeChange(-2)}
            className="w-7 h-7 flex items-center justify-center text-mute hover:bg-paper-2 rounded"
          >
            -
          </button>
          <span className="text-sm font-mono w-10 text-center">{fontSize}</span>
          <button
            onClick={() => handleFontSizeChange(2)}
            className="w-7 h-7 flex items-center justify-center text-mute hover:bg-paper-2 rounded"
          >
            +
          </button>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-paper-3" />

        {/* Color */}
        <div className="relative">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="flex items-center gap-2 p-1.5 border border-line rounded-control hover:border-primary"
          >
            <div
              className="w-6 h-6 rounded border border-line"
              style={{ backgroundColor: textColor }}
            />
            <Palette className="w-4 h-4 text-mute" />
          </button>

          {showColorPicker && (
            <div className="absolute bottom-full left-0 mb-2 z-50">
              <ColorPicker
                value={textColor}
                onChange={handleColorChange}
                onClose={() => setShowColorPicker(false)}
              />
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-paper-3" />

        {/* Style Buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleBoldToggle}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded transition-all',
              isBold
                ? 'bg-primary text-primary-fg'
                : 'text-ink-2 hover:bg-paper-2'
            )}
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            onClick={handleItalicToggle}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded transition-all',
              isItalic
                ? 'bg-primary text-primary-fg'
                : 'text-ink-2 hover:bg-paper-2'
            )}
          >
            <Italic className="w-4 h-4" />
          </button>
          <button
            onClick={handleUnderlineToggle}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded transition-all',
              isUnderline
                ? 'bg-primary text-primary-fg'
                : 'text-ink-2 hover:bg-paper-2'
            )}
          >
            <Underline className="w-4 h-4" />
          </button>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-paper-3" />

        {/* Alignment */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleAlignChange('left')}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded transition-all',
              textAlign === 'left'
                ? 'bg-primary text-primary-fg'
                : 'text-ink-2 hover:bg-paper-2'
            )}
          >
            <AlignLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleAlignChange('center')}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded transition-all',
              textAlign === 'center'
                ? 'bg-primary text-primary-fg'
                : 'text-ink-2 hover:bg-paper-2'
            )}
          >
            <AlignCenter className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleAlignChange('right')}
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded transition-all',
              textAlign === 'right'
                ? 'bg-primary text-primary-fg'
                : 'text-ink-2 hover:bg-paper-2'
            )}
          >
            <AlignRight className="w-4 h-4" />
          </button>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleDuplicate}
            className="p-2 text-mute hover:text-primary hover:bg-primary-50 rounded-control transition"
            title={t('creator.text.duplicate')}
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            className="p-2 text-mute hover:text-danger hover:bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] rounded-control transition"
            title={t('common.delete')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
